import { els } from './helpers';

/**
 * Tracks the pointer across the guitar-string hero pattern and drives the glow overlay's
 * position through two custom properties. The lighting itself is done in CSS with a
 * color-dodge blend (see general/string-pattern.css) - this only moves the gradient, so
 * the per-frame cost is two style writes regardless of how many tiles are on screen.
 */
class StringPatternGlow {
    /**
     * @param {HTMLElement} container The element the pointer is tracked against.
     * @param {HTMLElement} glow The overlay whose gradient position is written.
     */
    constructor(container, glow) {
        this.container = container;
        this.glow = glow;

        /* The lerp factor that makes the glow trail a little behind the cursor. 1 disables
         * the trail entirely, which is what reduced motion gets: the glow stays available
         * because it is user-driven, it just stops animating on its own. */
        this.ease = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 0.14;

        this.targetX = 0;
        this.targetY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.frame = null;
        this.isTracking = false;
    }

    init() {
        this.container.addEventListener('pointermove', (event) => {
            this._track(event);
        });

        this.container.addEventListener('pointerleave', () => {
            this._stop();
        });
    }

    _track(event) {
        const rect = this.container.getBoundingClientRect();

        this.targetX = event.clientX - rect.left;
        this.targetY = event.clientY - rect.top;

        // Snap on the first move so the glow appears under the cursor instead of sweeping
        // in from the centre of the hero.
        if (!this.isTracking) {
            this.isTracking = true;
            this.currentX = this.targetX;
            this.currentY = this.targetY;
            this.container.dataset.stringPatternActive = '';
        }

        this._start();
    }

    _start() {
        if (this.frame !== null) return;

        this.frame = requestAnimationFrame(() => {
            this._render();
        });
    }

    _render() {
        this.frame = null;

        this.currentX += (this.targetX - this.currentX) * this.ease;
        this.currentY += (this.targetY - this.currentY) * this.ease;

        this.glow.style.setProperty('--string-glow-x', `${this.currentX}px`);
        this.glow.style.setProperty('--string-glow-y', `${this.currentY}px`);

        // Coordinates are relative to the container's own box, so the glow stays anchored
        // to the pattern while the page scrolls underneath it - no scroll listener needed.
        // Once the trail has caught up there is nothing left to animate, so the loop ends
        // and only restarts on the next pointermove.
        const hasSettled = Math.abs(this.targetX - this.currentX) < 0.5 && Math.abs(this.targetY - this.currentY) < 0.5;

        if (!hasSettled) {
            this._start();
        }
    }

    _stop() {
        if (this.frame !== null) {
            cancelAnimationFrame(this.frame);
            this.frame = null;
        }

        this.isTracking = false;
        delete this.container.dataset.stringPatternActive;
    }
}

const initStringPattern = (
    containerSelector = '[data-string-pattern]',
    glowSelector = '[data-string-pattern-glow]'
) => {
    // Nothing to track without a hovering pointer. Bailing here means touch devices never
    // attach a listener at all, and the overlay stays at opacity 0.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    for (const container of els(containerSelector)) {
        const glow = container.querySelector(glowSelector);

        if (!glow) continue;

        new StringPatternGlow(container, glow).init();
    }
};

export default initStringPattern;
